import { useState, useRef, useEffect, useCallback } from 'react';
import { Tooltip } from 'react-tooltip';

import excelIcon from '../../images/excel_icon.svg';
import loadIcon from '../../images/loader-transparent.svg';
import noDocumentImage from '../../images/no-document-copy.png';

import styles from './FileUploadManager.module.css'

const HubSpotAttachmentModal = ({
    isOpen,
    onClose,
    api,
    popup,
    recordId,
    objectType,
    showToaster,
    maxSelectableAttachments,
    addUploadedHubSpotFiles,
}) => {

  const [attachmentSearchQuery, setAttachmentSearchQuery] = useState(''); // Search for attachments
  const [attachmentPage, setAttachmentPage] = useState(1);
  const attachmentPageSize = 5;
  const [selectedAttachments, setSelectedAttachments] = useState(new Set());
  const [isAttachmentsLoading, setIsAttachmentsLoading] = useState(false);
  const [isUploadingHubSpotFiles, setIsUploadingHubSpotFiles] = useState(false);
  const [selectedAttachmentDataMap, setSelectedAttachmentDataMap] = useState({}); // { [fileId]: attachmentData }
  const [attachmentError, setAttachmentError] = useState('');
  const [allAttachments, setAllAttachments] = useState([]); // All fetched attachments
  const [supportedFilesCount, setSupportedFilesCount] = useState(0); // Total supported files count
  const [attachments, setAttachments] = useState([]); // Current page attachments
  const [filteredAttachments, setFilteredAttachments] = useState([]);
  const downloadAbortControllerRef = useRef(null);
  
  // Filter attachments based on search query
  const getFilteredAttachmentList = () => {
    if (!attachmentSearchQuery.trim()) {
      return allAttachments;
    }
    const query = attachmentSearchQuery.toLowerCase();
    return allAttachments.filter(att => {
      const displayName = getAttachmentDisplayName(att).toLowerCase();
      return displayName.includes(query);
    });
  };

  useEffect(() => {
    const filtered = getFilteredAttachmentList();

    setFilteredAttachments(filtered);

    setAttachmentPage(1);
  }, [attachmentSearchQuery, allAttachments]);

  // Small icon for attachment row based on file type
  const getAttachmentIconSrc = (att) => {
    const ext = (att?.extension || '').toLowerCase();
    const ct = (att?.fileContentType || '').toLowerCase();
    if (ext === 'pdf' || ct === 'application/pdf') {
      return 'https://static.boldsign.com/202507081507-260076/img/661f0f2.svg';
    }
    if (ext === 'doc' || ext === 'docx' || ct.includes('word') || ct === 'application/msword' || ct === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
      return 'https://static.boldsign.com/202507081507-260076/img/ab4aec6.svg';
    }
    if (ext === 'xls' || ext === 'xlsx' || ct.includes('sheet') || ct === 'application/vnd.ms-excel' || ct === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
      return excelIcon;
    }
    if (ct.startsWith('image/') || ['png','jpg','jpeg'].includes(ext)) {
      return 'https://static.boldsign.com/202507081507-260076/img/46fb0b7.svg';
    }
    return 'https://static.boldsign.com/202507081507-260076/img/46fb0b7.svg';
  };

  const toggleAttachmentSelection = (fileId) => {
    const newSelection = new Set(selectedAttachments);
    const newDataMap = { ...selectedAttachmentDataMap };
    
    if (newSelection.has(fileId)) {
      newSelection.delete(fileId);
      delete newDataMap[fileId];
    } else {
      if (newSelection.size >= maxSelectableAttachments) {
        showToaster(`Maximum document count should be less than or equal to 20`, 'error');
        return;
      }
      newSelection.add(fileId);
      // Store the full attachment data from current attachments array
      const attachmentData = attachments.find(att => att.fileId === fileId);
      if (attachmentData) {
        newDataMap[fileId] = attachmentData;
      }
    }
    
    setSelectedAttachments(newSelection);
    setSelectedAttachmentDataMap(newDataMap);
  };

  const closeAttachmentModal = () => {
    if (downloadAbortControllerRef.current) {
      downloadAbortControllerRef.current.abort();
      downloadAbortControllerRef.current = null;
    }

    setIsAttachmentsLoading(false);
    setIsUploadingHubSpotFiles(false);
    setSelectedAttachments(new Set());
    setSelectedAttachmentDataMap({});
    onClose(false);
  };
  // Build display filename with extension when missing
  const getAttachmentDisplayName = (att) => {
    if (!att) return '';
    const name = att.fileName || '';
    const ext = (att.extension || '').toString().trim();
    if (!ext) return name;
    const lowerName = name.toLowerCase();
    const dotExt = `.${ext.toLowerCase()}`;
    return lowerName.endsWith(dotExt) ? name : `${name}.${ext}`;
  };

  // Handle pagination changes for attachments
  useEffect(() => {
    const filteredList = getFilteredAttachmentList();
    if (filteredList.length === 0) {
      setAttachments([]);
      return;
    }
    
    // Calculate pagination based on filtered attachments
    const startIndex = (attachmentPage - 1) * attachmentPageSize;
    const paginatedItems = filteredList.slice(startIndex, startIndex + attachmentPageSize);
    setAttachments(paginatedItems || []);
  }, [attachmentPage, allAttachments, attachmentPageSize, attachmentSearchQuery]);

  // HubSpot attachment handlers
  const fetchHubSpotAttachments = async () => {
    try {
      setIsAttachmentsLoading(true);
      setAttachmentError('');

      // Fetch all files in a single request
      const params = {
        objectId: recordId,
        objectType,
      };

      const response = await api.get(`/hubspot/get-hubspot-files`, { params });
      
      if (response.status === 200) {
        const data = await response.data;
        const items = (data && (data.items || data.Items)) || (Array.isArray(data) ? data : []);
        const supportedCount = (data && (data.supportedFilesCount ?? data.SupportedFilesCount)) ?? 0;
        
        // Store all attachments
        setAllAttachments(items || []);
        setFilteredAttachments(items || []);
        setSupportedFilesCount(supportedCount || 0);
        
        // Set first page of items
        const startIndex = (attachmentPage - 1) * attachmentPageSize;
        const paginatedItems = items.slice(startIndex, startIndex + attachmentPageSize);
        setAttachments(paginatedItems || []);
      } else {
        setAttachmentError('Failed to load attachments from HubSpot');
      }
    } catch (err) {
      console.error('Error fetching attachments:', err);
      setAttachmentError('Failed to load attachments. Please try again.');
    } finally {
      setIsAttachmentsLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
        fetchHubSpotAttachments();
    }
  }, [isOpen]);

  const handleAddHubSpotAttachments = async () => {
    if (selectedAttachments.size === 0) {
      return;
    }

    if (selectedAttachments.size > maxSelectableAttachments) {
      showToaster(`You can upload a maximum of ${maxSelectableAttachments} files.`, 'error');
      return;
    }

    const selectedAttachmentData = Array.from(selectedAttachments)
      .map(fileId => selectedAttachmentDataMap[fileId])
      .filter(att => att);

    if (selectedAttachmentData.some(att => att.fileSize > 25 * 1024 * 1024)) {
      showToaster(`File size should not be exceed more than 25MB`, 'error');
      return;
    }

    setIsUploadingHubSpotFiles(true);
    await new Promise(resolve => {
      if (typeof window !== 'undefined' && window.requestAnimationFrame) {
        window.requestAnimationFrame(resolve);
      } else {
        setTimeout(resolve, 0);
      }
    });

    const abortController = new AbortController();
    downloadAbortControllerRef.current = abortController;

    // Use cached attachment data which persists across pagination
    try {
      // Delegate to FileUploadManager's existing add/limit logic.
      const result = addUploadedHubSpotFiles
        ? await addUploadedHubSpotFiles(selectedAttachmentData, abortController.signal)
        : { addedCount: 0, skippedCount: selectedAttachmentData.length };

      if (result.addedCount > 0 && !abortController.signal.aborted) {
        closeAttachmentModal();
      }
    } catch (error) {
      console.error('Error adding HubSpot attachments:', error);
      showToaster('Failed to add some HubSpot attachments. Please try again.', 'error');
    } finally {
      setIsUploadingHubSpotFiles(false);
      if (downloadAbortControllerRef.current === abortController) {
        downloadAbortControllerRef.current = null;
      }
    }
  };
  
  if (!isOpen) return null;
    return (
        <>
            {isOpen && (
                <div 
                  className={styles.templateModalOverlay} 
                  onClick={(e) => {
                    if (e.target === e.currentTarget) {
                      closeAttachmentModal();
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      closeAttachmentModal();
                    }
                  }}
                  role="presentation"
                >
                  <div 
                    className={styles.templateModal}
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="attachment-modal-title"
                  >
                    {/* Header */}
                    <div className={styles.templateHeader}>
                      <h1 id="attachment-modal-title">Choose document from HubSpot</h1>
                      <div
                        role="button"
                        tabIndex={0}
                        className={styles.closeButton}
                        onClick={closeAttachmentModal}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            closeAttachmentModal();
                          }
                        }}
                        aria-label="Close modal"
                      >
                        <span className={`${styles.iconBase} ${styles.closeIcon} sfIconBase sfCloseIcon`}></span>
                      </div>
                    </div>
        
                    {/* Content */}
                    <div className={styles.modalBody}>
                      {/* Search Bar */}
                      <div className={styles.searchWithButtonContainer}>
                        <div className={styles.templateSearchContainer}>
                          <span className={`${styles.sfSearchIcon} sfIconBase sfSearchIcon`}></span>
                          <input
                            type="text"
                            className={styles.templateSearchInput}
                            placeholder="Search documents"
                            value={attachmentSearchQuery}
                            onChange={(e) => {
                              setAttachmentSearchQuery(e.target.value);
                              setAttachmentPage(1); // Reset to first page on search
                            }}
                          />
                          {attachmentSearchQuery && (
                            <button
                              type="button"
                              className={styles.clearSearchButton}
                              aria-label="Clear search"
                              title="Clear"
                              onClick={() => {
                                setAttachmentSearchQuery('');
                                setAttachmentPage(1);
                              }}
                            >
                              <span className={`${styles.iconBase} ${styles.closeIcon} sfIconBase sfCloseIcon`}></span>
                            </button>
                          )}
                        </div>
                      </div>
        
                      {/* Always-visible list header */}
                      <div className={styles.templateListHeader}>
                        <h3>Documents list</h3>
                      </div>
        
                      {/* Actions bar */}
                      <div className={styles.attachmentsBar}>
                        {/* Right-side selected summary and action */}
                        <div className={styles.attachmentsRight}>
                          {selectedAttachments.size > 0 && (
                            <>
                              <span className={styles.attachmentsSelectedInfo}>
                                Selected documents({selectedAttachments.size})
                                <button
                                  type="button"
                                  className={styles.emptyCreateLink}
                                  style={{ marginLeft: 8 }}
                                  onClick={() => setSelectedAttachments(new Set())}
                                >
                                  Clear selection
                                </button>
                              </span>
                              <button
                                className={styles.templateNextButton}
                                onClick={handleAddHubSpotAttachments}
                                disabled={isAttachmentsLoading || selectedAttachments.size > maxSelectableAttachments}
                                type="button"
                              >
                                Use document(s)
                              </button>
                            </>
                          )}
                        </div>
                      </div>
        
                      {/* Body states */}
                      {isAttachmentsLoading && !isUploadingHubSpotFiles ? (
                        <div className={styles.loadingSpinner}>
                          <img src={loadIcon} alt="Loading" className="spinner-circle" />
                        </div>
                      ) : attachmentError ? (
                        <div className={styles.noTemplates}>
                          <div className={styles.emptyState}>
                            <div className={styles.emptyContent}>
                              <div className={styles.emptyTitle}>Failed to load documents</div>
                              <div className={styles.emptyDesc}>{attachmentError}</div>
                            </div>
                            <button className={styles.emptyCreateLink} onClick={fetchHubSpotAttachments}>Retry</button>
                          </div>
                        </div>
                      ) : (filteredAttachments?.length || 0) === 0 ? (
                        <div className={styles.noTemplates}>
                          <div className={styles.emptyState}>
                            <img className={styles.emptyImage} src={noDocumentImage} alt="No documents" />
                            <div className={styles.emptyContent}>
                              <div className={styles.emptyTitle}>No documents available!</div>
                              <div className={styles.emptyDesc}>
                                It looks like you haven&apos;t uploaded any documents to your HubSpot local file yet. Once you do, they&apos;ll appear right here!
                              </div>
                            </div>
                          </div>
                        </div>
                       ) : (
                         <>
                          {/* Paged list (client-side pagination with search filter) */}
                          {(() => {
                            const filteredList = getFilteredAttachmentList();
                            const total = filteredAttachments.length;
                            const startIndex = (attachmentPage - 1) * attachmentPageSize;
                            const startDisplay = Math.min(startIndex + 1, Math.max(total, 1));
                            const endDisplay = Math.min(startIndex + (filteredAttachments?.length || 0), total);
                            const endIndex = startIndex + attachmentPageSize;
                            const pageItems = filteredAttachments.slice(startIndex, endIndex);
                            return (
                              <>
                                <div className={styles.templateList}>
                                  {pageItems.map((attachment) => {
                                    const isSelected = selectedAttachments.has(attachment.fileId);
                                    return (
                                      <div
                                        key={attachment.fileId}
                                        className={`${styles.templateListItem} ${isSelected ? styles.selectedAttachment : ''}`}
                                        onClick={() => toggleAttachmentSelection(attachment.fileId)}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            toggleAttachmentSelection(attachment.fileId);
                                          }
                                        }}
                                        role="checkbox"
                                        aria-checked={isSelected}
                                        tabIndex={0}
                                        style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '12px' }}
                                      >
                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                          <input type="checkbox" checked={isSelected} onChange={() => {}} tabIndex={-1} aria-hidden="true" />
                                        </div>
                                        <img src={getAttachmentIconSrc(attachment)} alt="type" width={16} height={16} style={{ marginLeft: 8, marginRight: 8 }} />
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                          <div className={styles.templateName} title={getAttachmentDisplayName(attachment)} style={{ fontWeight: 500 }}>
                                            {getAttachmentDisplayName(attachment)}
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                                <div className={styles.paginationBar}>
                                  <span className={styles.paginationInfo}>{`${startDisplay}-${endDisplay} of ${total}`}</span>
                                  <div className={styles.paginationActions}>
                                    <button
                                      className={styles.pageNavBtn}
                                      onClick={() => setAttachmentPage(p => Math.max(1, p - 1))}
                                      disabled={attachmentPage === 1}
                                      aria-label="Previous"
                                    >
                                      <span className="sfIconBase sfLeftArrowIcon"></span>
                                    </button>
                                    <button
                                      className={styles.pageNavBtn}
                                      onClick={() => setAttachmentPage(p => ((attachmentPage * attachmentPageSize) >= total ? p : p + 1))}
                                      disabled={(attachmentPage * attachmentPageSize) >= total}
                                      aria-label="Next"
                                    >
                                      <span className="sfIconBase sfRightArrowIcon"></span>
                                    </button>
                                  </div>
                                </div>
                              </>
                            );
                          })()}
                        </>
                      )}
                    </div>
                      
                    { isUploadingHubSpotFiles && (
                      <div className={styles.uploadingOverlay} role="status" aria-live="polite">
                        <img src={loadIcon} alt="" className="spinner-circle" />
                        <span>Uploading HubSpot files</span>
                      </div>
                    )}
                    {/* Footer intentionally hidden per UI requirement */}
                  </div>
                </div>
              )}
        </>
    );
};

export default HubSpotAttachmentModal;